import React, { useCallback } from 'react';
import { css } from '@compiled/react';

import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { CommentBatchList } from '@confluence/comment/entry-points/CommentBatchList';
import { getCommentThreadsToBatch } from '@confluence/comments-panel/entry-points/commentThreadHelper';
import { expValEquals } from '@confluence/feature-experiments';

import { ObjectCommentThread } from './ObjectCommentThread';

type CommentListProps = {
	commentThreads: CommentData[];
	supportedTopLevelActions: CommentAction[];
	flags?: FlagsStateContainer;
	onAllCommentsRendered?: () => void;
	onCommentBatchRendered?: () => void;
	isOffline?: boolean;
};

const listStyles = css({
	overflowY: 'visible', // Required for overflowing editor menus
	overflowX: 'visible', // Even though overflow is mostly in Y direction, overflow will break w/o this line
	flexGrow: 1,
	display: 'block',
});

// this component holds a list of comments
export const ObjectCommentsList = ({
	commentThreads,
	supportedTopLevelActions,
	flags,
	onAllCommentsRendered,
	onCommentBatchRendered,
	isOffline,
}: CommentListProps) => {
	const memoizedIsLastThread = useCallback(
		(index: number) => index === commentThreads.length - 1,
		[commentThreads],
	);

	const isLastThread = expValEquals(
		'cc_fe_performance_page_comments_rerender',
		'cohort',
		'experiment',
	)
		? memoizedIsLastThread
		: (index: number) => index === commentThreads.length - 1;

	// Props we want the batch renderer to pass through that aren't comment specific
	const passThroughProps = {
		supportedTopLevelActions,
		flags,
		onLastThreadRendered: onAllCommentsRendered,
		onCommentBatchRendered,
		isLastThread,
		isOffline,
	};

	return (
		<div
			id="object-comments-list-container"
			data-testid="object-comments-list-container"
			css={listStyles}
		>
			<CommentBatchList
				commentsList={commentThreads}
				passThroughProps={passThroughProps}
				Component={ObjectCommentThread}
				getElementsToBatch={getCommentThreadsToBatch}
			/>
		</div>
	);
};
